import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Select,
  Switch,
  Tooltip,
  Tag,
  Segmented,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  ToolOutlined,
  RightOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  PushpinOutlined,
  TeamOutlined,
  SafetyOutlined,
  RobotOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { SkillEditorDrawer } from './SkillEditorDrawer';
import { SkillImportDrawer } from './SkillImportDrawer';
import { openDeleteSkillModal } from './DeleteSkillModal';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import { useAgentRow } from '@/components/useAgentDirectory';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { AiSkill, AgentDefinition } from '@/api/types';
import { colors, typography } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// SkillsListPage — the heart of the skill catalog. Designed to communicate
// what a skill IS in this platform, not just dump rows from `ai.AiSkill`.
//
// Above the fold:
//   1. Hero header — what skills are + count + help hotspot.
//   2. Stats strip — total / active / Clinical Safety / always-load / orphaned.
//   3. Filter bar — search, category, "show only skills used by agent X",
//      active-only toggle, view toggle (Cards / Table), Refresh, Import.
//   4. Cards grid (default) or Table — same data, two presentations.
//
// Card view is the default because skills are AUTHORED ENTITIES — readable
// descriptions and visual signals (Clinical Safety tint, "Always-load" pin,
// "Used by N agents" badge) communicate more than a dense table can. Table
// stays as a power-user fallback for triage workflows.
//
// "Used by N agents" comes from the AgentLinkCount [NotMapped] projection
// added on the server (AiSkillRepository.GetQueryWithCounts) so we can render
// the badge inline without a per-row round-trip.
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_TONE: Record<string, PillTone> = {
  'Clinical Safety': 'red',
  Clinical: 'ink',
  'Clinical Context': 'violet',
  Protocol: 'violet',
  Operational: 'amber',
  Communication: 'green',
};

// Categories that are "load-bearing" for clinical safety — these surface with
// extra emphasis in the cards (red bar + safety icon).
const SAFETY_CATEGORIES = new Set(['Clinical Safety']);

type ViewMode = 'cards' | 'table';
const VIEW_MODE_KEY = 'agentstudio.skills.viewMode';

export const SkillsListPage = observer(function SkillsListPage() {
  const { skillStore: skills, themeStore } = useStore();
  const navigate = useNavigate();
  const isDark = themeStore.mode === 'dark';

  const [editId, setEditId] = useState<number | null>(null);
  const [editPreview, setEditPreview] = useState<AiSkill | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const v = window.localStorage.getItem(VIEW_MODE_KEY);
      return v === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });

  function setViewModePersisted(v: ViewMode): void {
    setViewMode(v);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, v);
    } catch {
      /* localStorage full / disabled — silently degrade. */
    }
  }

  useEffect(() => {
    if (skills.skills.length === 0 && !skills.isLoading) void skills.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Stats over the current result set ────────────────────────────────
  // Computed from the rows the server returned (already filtered by the
  // active toggles), so the strip reflects what the user is currently
  // looking at — not the platform-wide totals. Cheap O(n) scan over the
  // (typically ≤200) loaded rows.
  const stats = useMemo(() => {
    const all = skills.skills;
    const active = all.filter((s) => s.isActive !== false).length;
    const safety = all.filter((s) => SAFETY_CATEGORIES.has(s.category ?? '')).length;
    const alwaysLoad = all.filter((s) => s.alwaysLoad === true).length;
    const orphaned = all.filter((s) => (s.agentLinkCount ?? 0) === 0).length;
    return { total: all.length, active, safety, alwaysLoad, orphaned };
  }, [skills.skills]);

  // Which tile is currently "active" — drives the highlighted-tile state
  // in StatsStrip. Single-select (no multi-active) so the visual stays
  // unambiguous; matches the applyTileFilter snap-to behavior in the store.
  const activeTile: 'all' | 'active' | 'safety' | 'orphaned' | null =
    skills.orphanedOnly
      ? 'orphaned'
      : skills.category === 'Clinical Safety'
      ? 'safety'
      : !skills.activeOnly && skills.category === null
      ? 'all'
      : skills.activeOnly && skills.category === null
      ? 'active'
      : null;

  function handleTileClick(tile: 'all' | 'active' | 'safety' | 'orphaned'): void {
    skills.applyTileFilter(tile);
    void skills.fetch();
  }

  // ── Group cards by category for the cards view ────────────────────────
  // Clinical Safety first (the most consequential), then alphabetical.
  // Apply the orphan-only client-side filter here — the backend doesn't
  // expose an "agentLinkCount = 0" filter; cheaper to filter client-side
  // since the count is already on every row.
  const visibleSkills = useMemo(
    () =>
      skills.orphanedOnly
        ? skills.skills.filter((s) => (s.agentLinkCount ?? 0) === 0)
        : skills.skills,
    [skills.skills, skills.orphanedOnly],
  );

  const groupedCards = useMemo(() => {
    if (viewMode !== 'cards') return [];
    const buckets = new Map<string, AiSkill[]>();
    for (const s of visibleSkills) {
      const cat = s.category ?? '(Uncategorized)';
      const arr = buckets.get(cat) ?? [];
      arr.push(s);
      buckets.set(cat, arr);
    }
    return Array.from(buckets.entries()).sort(([a], [b]) => {
      // Clinical Safety always first; then alphabetical; "(Uncategorized)" last.
      if (SAFETY_CATEGORIES.has(a) && !SAFETY_CATEGORIES.has(b)) return -1;
      if (SAFETY_CATEGORIES.has(b) && !SAFETY_CATEGORIES.has(a)) return 1;
      if (a === '(Uncategorized)') return 1;
      if (b === '(Uncategorized)') return -1;
      return a.localeCompare(b);
    });
  }, [visibleSkills, viewMode]);

  // ── Helpers ──────────────────────────────────────────────────────────
  function openEdit(row: AiSkill): void {
    setEditId(row.id);
    setEditPreview(row);
  }

  function openDetail(row: AiSkill): void {
    trackEvent('Skill.Opened', { skillId: row.id });
    navigate(`/skills/${row.id}`);
  }

  function askDelete(row: AiSkill): void {
    openDeleteSkillModal(row, () => {
      trackEvent('Skill.Deleted', { skillId: row.id, name: row.name });
      void skills.fetch();
    });
  }

  // ── Table columns (preserved for the table-view fallback) ────────────
  const columns: ColumnsType<AiSkill> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => (
        <Text
          style={{
            color: isDark ? colors.slate[50] : colors.slate[900],
            fontWeight: 600,
            fontSize: 13.5,
            fontFamily: typography.fontMono,
          }}
        >
          {name}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (cat?: string) =>
        cat ? (
          <StatusPill tone={CATEGORY_TONE[cat] ?? 'slate'}>{cat}</StatusPill>
        ) : (
          <StatusPill tone="slate">—</StatusPill>
        ),
    },
    {
      title: 'Used by',
      dataIndex: 'agentLinkCount',
      key: 'agentLinkCount',
      width: 110,
      render: (n?: number) =>
        n && n > 0 ? (
          <Tag icon={<TeamOutlined />} color="blue" style={{ borderRadius: 999 }}>
            {n} agent{n === 1 ? '' : 's'}
          </Tag>
        ) : (
          <Tooltip title="Not currently linked to any agent — candidate for cleanup.">
            <Tag color="default" style={{ borderRadius: 999, opacity: 0.7 }}>
              Orphaned
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 90,
      render: (v?: string) => (
        <Text style={{ fontFamily: typography.fontMono, fontSize: 11.5, color: colors.slate[500] }}>
          {v ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v: boolean) =>
        v === false ? <StatusPill tone="slate">Inactive</StatusPill> : <StatusPill tone="green">Active</StatusPill>,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          <Tooltip title="Edit (with AI co-pilot)">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Tooltip title="Open detail page">
            <Link to={`/skills/${row.id}`}>
              <Button type="text" size="small" icon={<RightOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Delete (checks linked agents first)">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => askDelete(row)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // The agent filter chip needs a friendly display name when an agent is selected: the row the picker handed back,
  // else the directory's row for that id (a store page would not hold an agent past its first page).
  const [chosenAgent, setChosenAgent] = useState<AgentDefinition | undefined>();
  const selectedAgent = useAgentRow(skills.agentId, chosenAgent);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ToolOutlined />}
        title={
          <Space size={6} align="center">
            <span>Skills</span>
            <HelpHotspot topic="procedures.skills" />
          </Space>
        }
        subtitle="Reusable runbooks each agent loads on demand. Authored once, used by many."
        count={skills.totalCount}
        countLabel={skills.totalCount === 1 ? 'skill' : 'skills'}
      />

      {/* ── Stats strip ─────────────────────────────────────────────── */}
      <StatsStrip
        stats={stats}
        isDark={isDark}
        activeTile={activeTile}
        onTileClick={handleTileClick}
      />

      {/* ── Filter bar ──────────────────────────────────────────────── */}
      <SectionCard noPadding>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
        >
          <Input.Search
            placeholder="Search skills…"
            allowClear
            style={{ width: 240 }}
            value={skills.search}
            onChange={(e) => skills.setSearch(e.target.value)}
            onSearch={() => void skills.fetch()}
          />
          <Select
            allowClear
            placeholder="Any category"
            style={{ width: 170 }}
            value={skills.category ?? undefined}
            onChange={(v) => {
              skills.setCategory(v ?? null);
              void skills.fetch();
            }}
            options={[
              { value: 'Clinical Safety', label: 'Clinical Safety' },
              { value: 'Clinical', label: 'Clinical' },
              { value: 'Clinical Context', label: 'Clinical Context' },
              { value: 'Protocol', label: 'Protocol' },
              { value: 'Operational', label: 'Operational' },
              { value: 'Communication', label: 'Communication' },
            ]}
          />
          {/* Standard project-wide agent picker (drawer-based) — same
              component used in Activity, Recent runs, Scenarios. Has a
              sticky search + audience/status/router quick filters baked in,
              and scales gracefully past 100+ agents. */}
          <AgentPickerTrigger
            value={skills.agentId}
            onChange={(id, agent) => {
              setChosenAgent(agent);
              skills.setAgentId(id);
              void skills.fetch();
            }}
            placeholder="Any agent"
            drawerTitle="Filter skills by agent"
            clearLabel="Any agent — show all"
          />
          <Tooltip title="Hide retired skills">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: isDark ? colors.slate[300] : colors.slate[600],
              }}
            >
              <Switch
                size="small"
                checked={skills.activeOnly}
                onChange={(v) => {
                  skills.setActiveOnly(v);
                  void skills.fetch();
                }}
              />
              Active only
            </span>
          </Tooltip>

          <span style={{ flex: 1 }} />

          <Segmented
            value={viewMode}
            onChange={(v) => setViewModePersisted(v as ViewMode)}
            options={[
              { value: 'cards', icon: <AppstoreOutlined />, label: 'Cards' },
              { value: 'table', icon: <UnorderedListOutlined />, label: 'Table' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void skills.fetch()}
            loading={skills.isLoading}
          >
            Refresh
          </Button>
          <Tooltip title="Import a skill from a .md file">
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              Import from file
            </Button>
          </Tooltip>
        </div>

        {/* Active filter chips — quick way to clear individual filters */}
        {(skills.search || skills.category || skills.agentId || !skills.activeOnly) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              background: isDark ? colors.slate[800] : colors.slate[50],
              borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
              Filters:
            </Text>
            {skills.search && (
              <Tag
                closable
                onClose={() => {
                  skills.setSearch('');
                  void skills.fetch();
                }}
                color="blue"
                style={{ borderRadius: 999 }}
              >
                Search: <Text strong>{skills.search}</Text>
              </Tag>
            )}
            {skills.category && (
              <Tag
                closable
                onClose={() => {
                  skills.setCategory(null);
                  void skills.fetch();
                }}
                color="purple"
                style={{ borderRadius: 999 }}
              >
                Category: <Text strong>{skills.category}</Text>
              </Tag>
            )}
            {skills.agentId && (
              <Tag
                closable
                onClose={() => {
                  skills.setAgentId(null);
                  void skills.fetch();
                }}
                color="geekblue"
                icon={<RobotOutlined />}
                style={{ borderRadius: 999 }}
              >
                Used by: <Text strong>{selectedAgent?.displayName ?? `Agent ${skills.agentId}`}</Text>
              </Tag>
            )}
            {!skills.activeOnly && (
              <Tag
                closable
                onClose={() => {
                  skills.setActiveOnly(true);
                  void skills.fetch();
                }}
                color="default"
                style={{ borderRadius: 999 }}
              >
                Including retired
              </Tag>
            )}
          </div>
        )}
      </SectionCard>

      {skills.error && (
        <Alert type="error" message="Failed to load skills" description={skills.error} showIcon closable />
      )}

      {/* ── Body ────────────────────────────────────────────────────── */}
      {viewMode === 'cards' ? (
        <CardsView
          loading={skills.isLoading}
          grouped={groupedCards}
          isDark={isDark}
          onEdit={openEdit}
          onOpen={openDetail}
          onDelete={askDelete}
        />
      ) : (
        <SectionCard noPadding>
          <Table<AiSkill>
            rowKey="id"
            columns={columns}
            dataSource={visibleSkills}
            loading={skills.isLoading}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 25,
              showSizeChanger: true,
              showTotal: (t) => `${t} skill${t === 1 ? '' : 's'}`,
              style: { padding: '12px 18px', margin: 0 },
            }}
            onRow={(row) => ({
              onClick: () => openDetail(row),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <BrandedEmpty
                  icon={<ToolOutlined />}
                  title="No skills match"
                  description="Try clearing the category filter or widening with active-only off."
                />
              ),
            }}
          />
        </SectionCard>
      )}

      <SkillEditorDrawer
        skillId={editId}
        preview={editPreview}
        onClose={() => {
          setEditId(null);
          setEditPreview(null);
        }}
        onSaved={() => void skills.fetch()}
      />

      <SkillImportDrawer
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(result) => {
          trackEvent('Skill.Imported', { skillId: result.id, name: result.name, status: result.status });
          void skills.fetch();
        }}
      />
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Stats strip — five at-a-glance metrics over the current result set.
// ─────────────────────────────────────────────────────────────────────────────

interface StatsStripProps {
  stats: { total: number; active: number; safety: number; alwaysLoad: number; orphaned: number };
  isDark: boolean;
  /** Currently-applied tile filter so the right tile renders as "active". Drives the colored ring. */
  activeTile: 'all' | 'active' | 'safety' | 'orphaned' | null;
  onTileClick: (tile: 'all' | 'active' | 'safety' | 'orphaned') => void;
}

function StatsStrip({ stats, isDark, activeTile, onTileClick }: StatsStripProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 10,
      }}
    >
      <StatTile
        label="Total"
        value={stats.total}
        accent={colors.violet[600]}
        icon={<ToolOutlined />}
        tooltip="All skills. Click to clear category + active-only + orphaned filters."
        isDark={isDark}
        active={activeTile === 'all'}
        onClick={() => onTileClick('all')}
      />
      <StatTile
        label="Active"
        value={stats.active}
        accent={colors.green[600]}
        icon={<RightOutlined />}
        tooltip="Skills marked active. Inactive skills aren't loaded by agents. Click to filter."
        isDark={isDark}
        active={activeTile === 'active'}
        onClick={() => onTileClick('active')}
      />
      <StatTile
        label="Clinical Safety"
        value={stats.safety}
        accent={colors.red[500]}
        icon={<SafetyOutlined />}
        tooltip="Load-bearing safety skills. Click to filter to category = Clinical Safety."
        isDark={isDark}
        active={activeTile === 'safety'}
        onClick={() => onTileClick('safety')}
      />
      <StatTile
        label="Always-load"
        value={stats.alwaysLoad}
        accent={colors.amber[600]}
        icon={<PushpinOutlined />}
        tooltip="Skills the router includes on every turn for at least one agent. (Per-agent flag — only meaningful when filtering by an agent.)"
        isDark={isDark}
        // Always-load is a per-link flag, not a per-skill flag. The catalog
        // view never sees it populated, so the tile shows 0 and is left
        // non-clickable rather than wired to a filter that would always
        // produce zero results.
        muted
      />
      <StatTile
        label="Orphaned"
        value={stats.orphaned}
        accent={colors.slate[400]}
        icon={<CloseCircleOutlined />}
        tooltip="Skills not currently linked to any agent. Click to filter — strong candidates for cleanup."
        isDark={isDark}
        active={activeTile === 'orphaned'}
        onClick={() => onTileClick('orphaned')}
        muted={stats.orphaned === 0 && activeTile !== 'orphaned'}
      />
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: number;
  accent: string;
  icon: React.ReactNode;
  tooltip: string;
  isDark: boolean;
  muted?: boolean;
  active?: boolean;
  onClick?: () => void;
}

function StatTile({ label, value, accent, icon, tooltip, isDark, muted, active, onClick }: StatTileProps) {
  const interactive = !!onClick;
  return (
    <Tooltip title={tooltip}>
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          borderRadius: 12,
          background: active
            ? isDark
              ? `${accent}33`
              : `${accent}14`
            : isDark
            ? colors.slate[800]
            : '#fff',
          border: `${active ? 2 : 1}px solid ${
            active ? accent : isDark ? colors.slate[700] : colors.slate[200]
          }`,
          // Active tiles get a soft ring; inactive interactive tiles get
          // a subtle hover affordance via cursor.
          boxShadow: active ? `0 0 0 3px ${accent}22` : 'none',
          opacity: muted && !active ? 0.7 : 1,
          cursor: interactive ? 'pointer' : 'help',
          transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
          // Fix the height jump from the 1px → 2px border swap by using
          // a transparent inner border on inactive interactive tiles.
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 8,
            background: `${accent}1A`,
            color: accent,
            flexShrink: 0,
            fontSize: 15,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: isDark ? colors.slate[50] : colors.slate[900],
              lineHeight: 1.05,
            }}
          >
            {value}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        </div>
      </div>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cards view — grouped by category with rich cards per skill.
// ─────────────────────────────────────────────────────────────────────────────

interface CardsViewProps {
  loading: boolean;
  grouped: Array<[string, AiSkill[]]>;
  isDark: boolean;
  onEdit: (row: AiSkill) => void;
  onOpen: (row: AiSkill) => void;
  onDelete: (row: AiSkill) => void;
}

function CardsView({ loading, grouped, isDark, onEdit, onOpen, onDelete }: CardsViewProps) {
  if (loading && grouped.length === 0) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              height: 178,
              borderRadius: 12,
              background: isDark ? colors.slate[800] : colors.slate[100],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              opacity: 0.55,
            }}
          />
        ))}
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <SectionCard>
        <div style={{ padding: 32 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ maxWidth: 420, margin: '0 auto' }}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 6 }}>
                  No skills match these filters
                </Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                  Try clearing a filter, switching off &ldquo;Active only,&rdquo; or import a new
                  skill from a Markdown file using the button above.
                </Text>
              </div>
            }
          />
        </div>
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {grouped.map(([category, items]) => (
        <CategoryGroup
          key={category}
          category={category}
          items={items}
          isDark={isDark}
          onEdit={onEdit}
          onOpen={onOpen}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

interface CategoryGroupProps {
  category: string;
  items: AiSkill[];
  isDark: boolean;
  onEdit: (row: AiSkill) => void;
  onOpen: (row: AiSkill) => void;
  onDelete: (row: AiSkill) => void;
}

function CategoryGroup({ category, items, isDark, onEdit, onOpen, onDelete }: CategoryGroupProps) {
  const tone = CATEGORY_TONE[category] ?? 'slate';
  const isSafety = SAFETY_CATEGORIES.has(category);
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
          padding: '0 4px',
        }}
      >
        {isSafety && (
          <SafetyOutlined
            style={{ color: colors.red[500], fontSize: 16 }}
            aria-label="Clinical Safety category"
          />
        )}
        <Title level={5} style={{ margin: 0, fontSize: 15 }}>
          {category}
        </Title>
        <StatusPill tone={tone}>{items.length}</StatusPill>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((s) => (
          <SkillCard
            key={s.id}
            skill={s}
            isDark={isDark}
            onEdit={() => onEdit(s)}
            onOpen={() => onOpen(s)}
            onDelete={() => onDelete(s)}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SkillCard — the single most-touched component on this page. Designed to
// communicate the skill's purpose, importance, and reach in one glance.
// ─────────────────────────────────────────────────────────────────────────────

interface SkillCardProps {
  skill: AiSkill;
  isDark: boolean;
  onEdit: () => void;
  onOpen: () => void;
  onDelete: () => void;
}

function SkillCard({ skill, isDark, onEdit, onOpen, onDelete }: SkillCardProps) {
  const [hover, setHover] = useState(false);
  const cat = skill.category ?? '(Uncategorized)';
  const tone = CATEGORY_TONE[cat] ?? 'slate';
  const isSafety = SAFETY_CATEGORIES.has(cat);
  const accent =
    tone === 'red'
      ? colors.red[500]
      : tone === 'ink'
      ? colors.ink[600]
      : tone === 'violet'
      ? colors.violet[600]
      : tone === 'amber'
      ? colors.amber[600]
      : tone === 'green'
      ? colors.green[600]
      : colors.slate[400];
  const linkCount = skill.agentLinkCount ?? 0;
  const isOrphaned = linkCount === 0;
  const isInactive = skill.isActive === false;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      style={{
        position: 'relative',
        cursor: 'pointer',
        background: isDark ? colors.slate[800] : '#fff',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 12,
        padding: '14px 16px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        opacity: isInactive ? 0.65 : 1,
        boxShadow: hover
          ? `0 6px 16px ${isDark ? '#00000044' : '#0F172A14'}`
          : '0 1px 2px transparent',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'box-shadow 0.15s, transform 0.15s, border-color 0.15s',
        borderLeftColor: accent,
        borderLeftWidth: isSafety ? 4 : 1,
      }}
    >
      {/* Top row: name + version */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <Text
          style={{
            color: isDark ? colors.slate[50] : colors.slate[900],
            fontWeight: 600,
            fontSize: 13.5,
            fontFamily: typography.fontMono,
            wordBreak: 'break-word',
            lineHeight: 1.35,
          }}
        >
          {skill.name}
        </Text>
        {skill.version && (
          <Text
            style={{
              fontSize: 10.5,
              color: colors.slate[500],
              fontFamily: typography.fontMono,
              flexShrink: 0,
            }}
          >
            v{skill.version}
          </Text>
        )}
      </div>

      {/* Description — the readable, router-facing summary. */}
      <Text
        type="secondary"
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {skill.description}
      </Text>

      {/* Footer: badges + actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
          marginTop: 'auto',
          minHeight: 28,
        }}
      >
        <StatusPill tone={tone}>{cat}</StatusPill>

        {linkCount > 0 ? (
          <Tooltip title={`Linked to ${linkCount} agent${linkCount === 1 ? '' : 's'}.`}>
            <Tag
              icon={<TeamOutlined />}
              color="blue"
              style={{ borderRadius: 999, fontSize: 11.5 }}
            >
              {linkCount} agent{linkCount === 1 ? '' : 's'}
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Not currently used by any agent. Either link it to an agent or retire it.">
            <Tag
              color="default"
              style={{ borderRadius: 999, fontSize: 11.5, opacity: 0.75 }}
            >
              Orphaned
            </Tag>
          </Tooltip>
        )}

        {skill.alwaysLoad === true && (
          <Tooltip title="Always loaded — bypasses the skill router on every turn for at least one agent.">
            <Tag
              icon={<PushpinOutlined />}
              color="orange"
              style={{ borderRadius: 999, fontSize: 11.5 }}
            >
              Always-load
            </Tag>
          </Tooltip>
        )}

        {isInactive && (
          <Tooltip title="Inactive — not loaded by agents.">
            <Tag color="default" style={{ borderRadius: 999, fontSize: 11.5 }}>
              Retired
            </Tag>
          </Tooltip>
        )}

        <span style={{ flex: 1 }} />

        {/* Actions appear on hover (or on touch — onTouchStart triggers focus). */}
        <Space
          size={2}
          style={{
            opacity: hover ? 1 : 0.55,
            transition: 'opacity 0.15s',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit (with AI co-pilot)">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            />
          </Tooltip>
          <Tooltip title="Delete (checks linked agents first)">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Orphaned skills get a subtle "needs attention" stripe — surfaces the
          cleanup signal without yelling. The user can dismiss by linking the
          skill to an agent or by deleting it. */}
      {isOrphaned && !isInactive && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: colors.amber[100],
            color: colors.amber[700],
            border: `1px solid ${colors.amber[500]}33`,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            padding: '2px 6px',
            borderRadius: 4,
            pointerEvents: 'none',
          }}
        >
          Unused
        </div>
      )}
    </div>
  );
}
